<template>
    <div class="modal-card card">
        <div class="card-header">
            <h4 class="card-header-title">{{ title }}</h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="card-body">
            <div v-if="showWarningPage">
                <div class="warning-section">
                    <p class="mb-0"><strong>Warning:</strong> The following domains ownership validation records have expired. Do you want to refresh?</p>
                </div>
                <div class="cname-list">
                    <div class="cname-item" v-for="item in expiredCNames" :key="item.id">
                        <div class="d-flex align-items-center">
                            <div class="cname-item-name">{{ item.name }}</div>
                        </div>
                        <div class="cname-item-value"
                            :title="'Expired on ' + item.validationDetails?.validation_expiry">
                            <strong>Expired:</strong>
                            {{ DateTimeFormat(item.validationDetails?.validation_expiry) }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-center py-3 bg-light rounded border border-dashed">
              <span class="text-muted">
                <i class="fa fa-info-circle"></i>
                {{ description }}
              </span>
            </div>
        </div>

        <div class="card-footer">
            <div v-if="showWarningPage" class="text-end">
                <button type="button" class="btn btn-primary" @click="showWarningPage = false">
                    Proceed
                    <i class="fa fa-angle-right ms-1"></i>
                </button>
            </div>
            <div v-else class="d-flex justify-content-end gap-2">
                <button type="button" class="btn btn-secondary" :disabled="loading" @click="onCancel">
                    Cancel
                </button>
                <button type="button" class="btn btn-outline-primary" :disabled="loading" @click="onSelect('CNAME')">
                    <span v-if="loadingMethod === 'CNAME'" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    {{ cnameLabel }}
                </button>
                <button type="button" class="btn btn-primary" :disabled="loading" @click="onSelect('DNS_TXT')">
                    <span v-if="loadingMethod === 'DNS_TXT'" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    {{ txtLabel }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTimeFormat } from "../../utils/commonFunction";
export default {
    name: "DomainRefreshMethod",
    props: {
        modalData: {
            type: Object,
            default: null,
        },
    },
    computed: {
        title() {
            return this.modalData?.title ?? "Refresh DNS records";
        },
        description() {
            return this.modalData?.description ?? "Please choose the validation method to be used for validation.";
        },
        cnameLabel() {
            return this.modalData?.cnameLabel ?? "DNS CNAME";
        },
        txtLabel() {
            return this.modalData?.txtLabel ?? "DNS TXT";
        },
        loading() {
            return Boolean(this.loadingMethod);
        },
    },
    data() {
        return {
            loadingMethod: null,
            expiredCNames: this.modalData?.expiredCNames ?? null,
            showWarningPage: this.modalData?.expiredCNames?.length > 0 ?? false,
        };
    },
    methods: {
        DateTimeFormat,
        onCancel() {
            if (typeof this.modalData?.onCancel === "function") {
                this.modalData.onCancel();
            }
            this.$store.dispatch("hideModal");
        },
        async onSelect(method) {
            this.loadingMethod = method;
            try {
                if (typeof this.modalData?.onSelect === "function") {
                    await this.modalData.onSelect(method);
                }
            } finally {
                this.loadingMethod = null;
            }
        },
    },
};
</script>
<style scoped>
.warning-section {
    background-color: #fff3cd;
    border-left: 4px solid #ffc107;
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
}

/* Container for the records */
.cname-list {
    max-height: 280px;
    overflow-y: auto;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background-color: #f8f9fa;
}

/* Individual Row */
.cname-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 15px;
    border-bottom: 1px solid #e9ecef;
    transition: background-color 0.2s ease;
}

.cname-item:last-child {
    border-bottom: none;
}

.cname-item:hover {
    background-color: #f1f4f8;
}

/* DNS Record Name Styling */
.cname-item-name {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.875rem;
    font-weight: 600;
    color: #2c3e50;
    word-break: break-all;
    padding-right: 15px;
}

/* Expiry Date Styling */
.cname-item-value {
    font-size: 0.75rem;
    color: #dc3545;
    /* Red to signify urgency/expiry */
    background-color: rgba(220, 53, 69, 0.1);
    padding: 2px 8px;
    border-radius: 12px;
    white-space: nowrap;
}

/* Custom Scrollbar for the list */
.cname-list::-webkit-scrollbar {
    width: 6px;
}

.cname-list::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
}
</style>
<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .warning-section {
        background-color: rgba(255, 193, 7, 0.1);
        border-left-color: #f59e0b;
        color: $dark-text-primary;
    }

    .cname-list {
        background-color: rgba(0, 0, 0, 0.2);
        border-color: rgba(255, 255, 255, 0.1);
    }

    .cname-item {
        border-bottom-color: $dark-border-color;

        &:hover {
            background-color: $dark-elevated-bg;
        }
    }

    .cname-item-name {
        color: $dark-text-primary;
    }
}
</style>
